import {
  IonContent,
  IonModal,
  IonNav,
  IonPage
} from '@ionic/react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import GolfClubSearch from '../GolfClubSearch';
import GameSetupPlayers from './GameSetupPlayers';
import { GameSetup as GameSetupType } from './types';
import GameSetupIntro from './GameSetupIntro';
import GameFormComponent from './GameFormComponent';

interface GameSetupProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: Partial<GameSetupType>;
  skipClubSelection?: boolean;
}

const GameSetup: React.FC<GameSetupProps> = ({ isOpen, onClose, initialData, skipClubSelection = false }) => {
  const { t } = useTranslation();
  const modal = useRef<HTMLIonModalElement>(null);
  const nav = useRef<HTMLIonNavElement>(null);
  const history = useHistory();
  const [gameData, setGameData] = useState<Partial<GameSetupType>>(initialData || {
    name: '',
    date: new Date().toISOString(),
    holes: 18,
    startingHole: 1,
    format: 'strokeplay',
    players: [],
    contests: {
      longestDrive: false,
      closestToPin: false,
    },
    teeOptions: {
      default: 'white',
    },
    useHandicaps: true,
    isPrivate: false,
  });

  const resetState = () => {
    // Reset the navigation stack to the root component
    if (nav.current) {
      nav.current.setRoot(WelcomeScreenPage, { nav: nav.current });
    }
    setGameData({
      name: '',
      date: new Date().toISOString(),
      holes: 18,
      startingHole: 1,
      format: 'strokeplay',
      players: [],
      contests: {
        longestDrive: false,
        closestToPin: false,
      },
      teeOptions: {
        default: 'white',
      },
      useHandicaps: true,
      isPrivate: false,
    });
  };



  const handleStartGame = () => {
    console.log('Starting game with data:', gameData);

    onClose();
    resetState();

    history.push('/play-game');
  };

  // Create component wrappers for IonNav
  const WelcomeScreenPage = ({ nav }: { nav: HTMLIonNavElement }) => {
    return (
      <IonPage className="ion-page">
        <GameSetupIntro
          onCreateGame={() => nav.push(GolfClubSearchPage, { nav })}
          onClose={() => onClose()}
          onJoinGame={() => console.log('Join game functionality not implemented yet')}
        />
      </IonPage>
    );
  };

  const GolfClubSearchPage = ({ nav }: { nav: HTMLIonNavElement }) => {
    return (
      <GolfClubSearch
        onCourseSelect={(club, course) => {
          const updatedGameData = {
            ...gameData,
            club: club as any,
            course: (course || (club.courses && club.courses.length === 1 ? club.courses[0] : null)) as any,
            name: gameData.name || `${club.name}`
          };

          setGameData(updatedGameData as any);

          nav.push((props: any) => <GameSetupFormPage {...props} initialData={updatedGameData as any} nav={nav} />);
        }}
        mode="Select"
        inline={true}
        selectCourse={true}
        customSearchCaption={t('search_golf_course_placeholder')}
        backHandler={() => {
          nav.pop();
          return true;
        }}
      />
    );
  };

  const GameSetupFormPage = ({ nav, initialData }: { nav: HTMLIonNavElement, initialData: GameSetupType }) => {
    return (
      <GameFormComponent
        initialData={initialData}
        nav={nav}
        onNext={(data) => {
          setGameData({ ...gameData, ...data });
          nav.push((props: any) => <AddPlayersPage {...props} nav={nav} initialData={{ ...gameData, ...data }} />);
          return true;
        }}
        backHandler={() => {
          nav.pop();
          return true;
        }}
      />
    );
  };

  const AddPlayersPage = ({ nav, initialData }: { nav: HTMLIonNavElement, initialData: GameSetupType }) => {
    const history = useHistory();

    return (
      <IonPage>
        <GameSetupPlayers
          initialData={initialData as any}
          nav={nav}
          onNext={(data) => {
            console.log("data", data)
            if (data.gameId) {
              // Navigate to the play route with the new game ID
              history.push(`/play/${data.gameId}`);

              // Close the modal using the modal ref
              modal.current?.dismiss();
            }


          }}
          onBack={() => {
            nav.pop();
            return true;
          }}
        />
      </IonPage>
    );
  };

  // Function to handle modal presentation and set the root component
  const didPresent = () => {
    if (nav.current) {
      // Enable swipe gestures for better mobile experience
      nav.current.swipeGesture = true;

      // If skipClubSelection is true and we have initialData with a club,
      // skip directly to the GameSetupForm page
      if (skipClubSelection && initialData?.club) {
        nav.current.setRoot((props: any) => <GameSetupFormPage {...props} initialData={gameData as any} nav={nav.current} />);
      } else {
        // Otherwise, start with the welcome screen as usual
        nav.current.setRoot(WelcomeScreenPage, { nav: nav.current });
      }
    }
  };



  return (
    <IonModal
      ref={modal}
      isOpen={isOpen}
      onDidDismiss={() => {
        onClose();
        resetState();
      }}
      onDidPresent={didPresent}
      style={{ '--width': '100%', '--height': '100%', '--max-width': '768px' }}
      className='md:py-4'
    >
      <IonContent scroll-y="false" className='py-4'>
        <IonNav ref={nav} />
      </IonContent>
    </IonModal>
  );
};

export default GameSetup;